// Écran de connexion / inscription. Un seul écran, avec bascule entre les deux modes
// (parcours linéaire, cf. règles UX). Validation côté navigateur via les schémas Zod
// partagés, puis le serveur revalide de toute façon.

import { useState, type FormEvent } from 'react'
import { useAuth } from '@/lib/auth'
import { ApiError } from '@/lib/api'
import { registerSchema, loginSchema } from '@shared/schemas/auth'
import { TRIAL_DURATION_DAYS } from '@shared/constants'

type Mode = 'login' | 'register'

function messageFromError(e: unknown): string {
  if (
    e instanceof ApiError &&
    e.body &&
    typeof e.body === 'object' &&
    'error' in e.body &&
    typeof (e.body as { error?: unknown }).error === 'string'
  ) {
    return (e.body as { error: string }).error
  }
  if (e instanceof Error) return e.message
  return 'Une erreur est survenue. Réessaie dans un instant.'
}

export default function AuthPage() {
  const { login, register } = useAuth()
  const [mode, setMode] = useState<Mode>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const isRegister = mode === 'register'

  function switchMode() {
    setMode(isRegister ? 'login' : 'register')
    setError(null)
    setPassword('')
    setConfirm('')
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)

    if (isRegister) {
      const parsed = registerSchema.safeParse({ email, password })
      if (!parsed.success) {
        setError(parsed.error.issues[0]?.message ?? 'Vérifie tes informations.')
        return
      }
      if (password !== confirm) {
        setError('Les deux mots de passe ne sont pas identiques.')
        return
      }
    } else {
      const parsed = loginSchema.safeParse({ email, password })
      if (!parsed.success) {
        setError(parsed.error.issues[0]?.message ?? 'Vérifie tes informations.')
        return
      }
    }

    setBusy(true)
    try {
      if (isRegister) {
        await register(email, password)
      } else {
        await login(email, password)
      }
      // En cas de succès, l'AuthProvider bascule l'écran : rien à faire ici.
    } catch (err) {
      setError(messageFromError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen bg-bdspBg flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 border border-stone-200">
        <img src="/logo-bdsp.png" alt="BD-SapoPro" className="mx-auto h-16 w-auto" />
        <p className="text-stone-600 text-center mt-2 mb-6">
          {isRegister ? 'Crée ton compte pour commencer.' : 'Bonjour.'}
        </p>

        {isRegister && (
          <div className="mb-6 rounded-lg border border-bdspIndigo bg-bdspIndigoSoft px-4 py-3 text-sm text-bdspInk">
            <strong>{TRIAL_DURATION_DAYS} jours d'essai gratuit</strong>, sans carte bancaire.
            Tu profites de toutes les fonctionnalités.
          </div>
        )}

        {error && (
          <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-bdspInk mb-1">
              Adresse e-mail
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-stone-300 px-4 py-3 text-stone-800 focus:border-bdspIndigo focus:outline-none focus:ring-2 focus:ring-bdspIndigo/30"
              placeholder="prenom@exemple.fr"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-bdspInk mb-1">
              Mot de passe
            </label>
            <input
              id="password"
              type="password"
              autoComplete={isRegister ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-stone-300 px-4 py-3 text-stone-800 focus:border-bdspIndigo focus:outline-none focus:ring-2 focus:ring-bdspIndigo/30"
              placeholder={isRegister ? 'Au moins 12 caractères' : 'Ton mot de passe'}
            />
          </div>

          {isRegister && (
            <div>
              <label htmlFor="confirm" className="block text-sm font-medium text-bdspInk mb-1">
                Confirme le mot de passe
              </label>
              <input
                id="confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full rounded-lg border border-stone-300 px-4 py-3 text-stone-800 focus:border-bdspIndigo focus:outline-none focus:ring-2 focus:ring-bdspIndigo/30"
                placeholder="Le même mot de passe"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-lg bg-bdspIndigo px-4 py-3 text-base font-semibold text-white shadow-sm transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? 'Un instant…' : isRegister ? 'Créer mon compte' : 'Se connecter'}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-stone-600">
          {isRegister ? 'Tu as déjà un compte ?' : 'Pas encore de compte ?'}{' '}
          <button
            type="button"
            onClick={switchMode}
            className="font-semibold text-bdspInk underline underline-offset-2 hover:text-bdspIndigo"
          >
            {isRegister ? 'Se connecter' : 'Créer un compte'}
          </button>
        </p>
      </div>
    </div>
  )
}
